import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, DragEvent, ReactNode, PointerEvent as ReactPointerEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useVault } from '../session/useVault';
import { usePlan } from '../plan/usePlan';
import { useCatalog } from '../entity/catalogStore';
import { useTheme } from '../design/useTheme';
import { CARD_WIDTH, canvasGround, markInk } from '../design/tokens';
import { tagTones } from '../design/tagPalette';
import { EMPTY_REGISTRIES, tagTint } from '../plan/registries';
import { pendingText } from '../vault/writeQueue';
import { useWorkspaces } from '../workspaces/useWorkspaces';
import { asTheyStand } from '../workspaces/loadWorkspaces';
import { usePinning } from '../workspaces/usePins';
import { LINE, NOOK_WIDTH, STICKY_SIZE, arcTitleOf, whenHeightSettles } from '../plane/cardSize';
import { refKey } from '../plane/planeKey';
import { useOpenOrder } from '../plane/openOrder';
import { Presence } from '../plane/Presence';
import { parseEntity } from '../entity/entity';
import type { PlanePlacement, PlaneRef, StickyTint } from '../plane/planeKey';
import type { TagColorId } from '../design/tagPalette';
import { FIRST_TINT, StickyPanel, noteTone, pendingTint } from '../nook/StickyPanel';
import { LinkButton, NoteButton, PageBar } from '../plane/PageBar';
import { Tray, TrayBrowse, TrayCardMenu, TrayList, TRAY_WIDTH, useTrayLifted } from '../plane/Tray';
import type { TrayEntry, TrayKind } from '../plane/Tray';
import { recencyOf } from '../plan/chronology';
import { Table } from '../plane/Table';
import { TABLE, boundsOf } from '../plane/tableFit';
import { useCardSizes } from '../plane/useCardSizes';
import { useTable } from '../plane/useTable';
import { useLinks } from '../plane/useLinks';
import { hrefFor } from '../routes/EntityPage';
import { NookCard } from '../nook/NookPage';
import { NookItemCard } from '../nook/NookItemCard';
import { REF_MIME } from '../nook/dnd';
import { arcsCiting, cardName, cardView, collapsedLinesOf, itemKind, wholeVaultRows } from '../nook/nook';
import { buildCorpus, itemFor } from '../thoughts/corpus';
import { isArcIn } from '../vault/kinds';
import { NewArcFlow } from '../nook/NewArcFlow';
import type { RecentRecording } from '../nook/NewArcFlow';
import { nameTaken, newArcFile } from '../nook/newArc';
import type { NewArc } from '../nook/newArc';
import { capturedLabel, recordingFace } from '../nook/nook';
import { monthPrefix, newHeldNoteFile, newNotePath } from '../writing/textNote';
import type { MintedNote } from '../writing/useNoteText';
import { newUlid } from '../entity/catalog';
import type { CardView, ItemKind, Row } from '../nook/nook';
import { BarHang, CardHang, pageSize, useCloseOnGround } from '../plane/Hang';
import { ArcWindow, NookWindow, NoteWindow, RecordingWindow } from '../nook/NookWindows';
import { hangLeftOf, hangUnder } from '../plane/hangPlace';
import { heldArcsOf, nameOf, startWithOf } from '../workspaces/workspace';
import { boxOf, deskMembers, freeSpots, recentDrafts } from './desk';
import type { DeskItem, SpotBox } from './desk';
import {
  archiveFromDesk,
  asWrites,
  deskToShelf,
  keepDraft,
  letGoDraft,
  moveOnDesk,
  offDesk,
  openOnDesk,
  placeOnDesk,
  shelfToDesk,
  linkOnDesk,
  sizeOnDesk,
  tintOnDesk,
  unpinOnDesk,
  unpinWithoutContainer,
} from './deskActs';
import type { DeskChange } from './deskActs';
import { DESK_PATH, readDeskContainer } from './deskContainer';
import { useDeskContainer } from './useDeskContainer';
import './desk.css';

const WIDTH: Record<ItemKind, number> = { recording: CARD_WIDTH, arc: CARD_WIDTH, note: 220, nook: NOOK_WIDTH.small, other: 260 };
const SLOP = 4;
interface Press {
  key: string;
  grab: { dx: number; dy: number };
  start: { x: number; y: number };
  moved: boolean;
}

/**
 * The desk, as the web's home: the cards the desk container holds, on a table of their own, with
 * the shelf and the drafts in a tray beside it. Cards rest collapsed unless their
 * plane line says `open`, and nothing moves by itself: a card with no plane line is drawn at a
 * first free spot in memory until the person moves it.
 *
 * Without a container the desk is read-only, drawn from what the nooks say (pinned); the
 * phone makes the container, and nothing here ever writes one.
 */
export function DeskPage() {
  const navigate = useNavigate();
  const { state, write, commit, queue } = useVault();
  const { plan: planState } = usePlan();
  const arcs = planState.status === 'ready' ? planState.plan.arcs : null;
  const registries = planState.status === 'ready' ? planState.plan.registries : EMPTY_REGISTRIES;
  const catalog = useCatalog();
  const { theme } = useTheme();
  const nooks = useWorkspaces();
  const workspaces = nooks.status === 'ready' ? nooks.workspaces : [];
  const { pin } = usePinning(workspaces);
  const { file, show } = useDeskContainer();
  const containerRaw = file.status === 'read' ? file.raw : null;
  const container = useMemo(() => readDeskContainer(containerRaw), [containerRaw]);
  const writable = container !== null;
  /** The container's read has an outcome: until it has, the desk is neither writable nor read-only,
   *  and nothing that depends on which it is is offered. */
  const known = file.status === 'read';

  const members = useMemo(() => deskMembers(container, workspaces), [container, workspaces]);
  const [allDrafts, setAllDrafts] = useState(false);
  const tray = useMemo(() => recentDrafts(members.drafts, Date.now()), [members.drafts]);
  const trayShown = allDrafts ? members.drafts : tray.shown;
  /** What is open off a card, a shelf card or a list row: one surface at a time. */
  const [detail, setDetail] = useState<{ ref: PlaneRef; where: 'table' | 'tray' } | null>(null);
  const nooksSeen = useRef(workspaces);
  nooksSeen.current = workspaces;
  /** The nooks an act's container write reads what the desk shows from, when it goes out. */
  const nooksNow = useCallback(() => asTheyStand(nooksSeen.current), []);
  const [trayOpen, setTrayOpen] = useState(false);
  const [trayKind, setTrayKind] = useState<TrayKind>('drafts');
  const [trayLifted, toggleLift] = useTrayLifted();
  const [query, setQuery] = useState('');
  const [trayEl, setTrayEl] = useState<HTMLElement | null>(null);
  const [making, setMaking] = useState(false);
  const kinds = useMemo<TrayKind[]>(
    () => [...(members.drafts.length > 0 ? (['drafts'] as const) : []), ...(members.shelf.length > 0 ? (['shelf'] as const) : []), 'nooks', 'recordings', 'notes', 'arcs'],
    [members.drafts.length, members.shelf.length],
  );
  const kind = kinds.includes(trayKind) ? trayKind : kinds[0]!;
  const dated = kind === 'recordings' || kind === 'notes' || kind === 'arcs';
  const corpus = useMemo(() => (state.status === 'ready' && trayOpen && (dated || making) ? buildCorpus(state.vault) : []), [state, trayOpen, dated, making]);
  /** What the vault lists for the open kind, newest first or the best matches, with what is on the desk
   *  held among them. */
  const vaultRows = useMemo(() => {
    if (!trayOpen || !(dated || kind === 'nooks')) return [];
    const onDesk = members.desk.filter((i) => !i.nook);
    return wholeVaultRows({
      panel: kind,
      nookPath: DESK_PATH,
      arcPaths: onDesk.filter((i) => isArcIn(i.ref.ref, arcs)).map((i) => i.ref.ref),
      members: onDesk.map((i) => i.ref),
      arcs,
      corpus,
      nooks: workspaces,
      catalog,
      query,
      nowMs: Date.now(),
    });
  }, [trayOpen, dated, kind, members.desk, corpus, arcs, catalog, query, workspaces]);
  const plane = container?.plane ?? [];
  const placements = useMemo(() => new Map(plane.map((p) => [refKey(p), p])), [plane]);
  const onCanvas = members.desk;

  const [files, setFiles] = useState<ReadonlyMap<string, string | null>>(new Map());
  const wanted = useMemo(
    () => [...new Set([...members.desk, ...members.shelf, ...members.drafts].map((i) => i.ref.ref).concat(detail ? [detail.ref.ref] : []).concat(vaultRows.filter((r) => !isArcIn(r.ref.ref, arcs)).map((r) => r.ref.ref)))].sort(),
    [members, detail, vaultRows, arcs],
  );
  const wantedKey = wanted.join('\n');
  const queueState = queue.state;
  useEffect(() => {
    if (state.status !== 'ready' || wanted.length === 0) return;
    let cancelled = false;
    void state.vault.readFiles(wanted).then((read) => {
      if (cancelled) return;
      const byPath = new Map(read.map((r) => [r.path, r.text ?? null]));
      setFiles(new Map(wanted.map((p) => [p, pendingText(p) ?? byPath.get(p) ?? null])));
    });
    return () => {
      cancelled = true;
    };
    // `wanted` is tracked by its key: a new array with the same paths is not a reason to read again.
  }, [state, wantedKey, queueState]);

  const [opening, setOpening] = useState<string | null>(null);
  /** The cards opened on a desk that cannot be written: this page's alone, and never written. */
  const [openHere, setOpenHere] = useState<ReadonlySet<string>>(() => new Set());
  const cards = useMemo(
    () =>
      onCanvas.flatMap((item) => {
        const placement = placements.get(item.key);
        const view = cardView(item.ref, arcs, files, placement?.tint, catalog);
        return view ? [{ item, view, placement }] : [];
      }),
    [onCanvas, placements, arcs, files, catalog],
  );
  const isOpen = (key: string) =>
    opening === key || (writable ? placements.get(key)?.open === true : known && openHere.has(key));

  const widthOf = useCallback(
    (key: string, kind: ItemKind) => (kind === 'nook' && isOpen(key) ? NOOK_WIDTH.open : WIDTH[kind]),
    // `isOpen` reads what these do.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [placements, opening, openHere, writable, known],
  );

  const surface = useRef<HTMLDivElement>(null);
  const sizes = useCardSizes(surface, cards);
  const heightOf = (key: string, item: DeskItem, view: CardView) =>
    sizes.get(key)?.h ?? collapsedLinesOf(item.ref, view, files.get(item.ref.ref)) * LINE;
  /** The table's inner width, which a card landing in a free gap may use. */
  const innerWidth =
    window.innerWidth - 2 * TABLE.gutter - 2 * TABLE.pad - (trayOpen && !trayLifted ? TRAY_WIDTH : 0);

  /** Where each card without a plane line landed, once it had: held for as long as the page is
   *  open, so no card is drawn closing up behind one that was moved, kept or let go. */
  const landedAt = useRef(new Map<string, { x: number; y: number }>());

  /** Where each card is drawn: its plane line, else the gap it landed in, else the first free gap
   *  found in memory. */
  const spots = useMemo(() => {
    const placed: SpotBox[] = [];
    const loose: { key: string; w: number; h: number }[] = [];
    const held = new Map<string, { x: number; y: number }>();
    for (const { item, view, placement } of cards) {
      const w = widthOf(item.key, view.kind);
      if (placement) {
        const box = boxOf(placement, view.kind, w);
        const size = sizes.get(item.key);
        placed.push(size ? { ...box, w: size.w, h: size.h } : box);
      } else {
        const was = landedAt.current.get(item.key);
        if (was) {
          held.set(item.key, was);
          placed.push({ ...was, w, h: heightOf(item.key, item, view) });
        } else loose.push({ key: item.key, w, h: heightOf(item.key, item, view) });
      }
    }
    return new Map([...held, ...freeSpots(placed, loose, innerWidth)]);
    // `heightOf` reads the same sizes and files this is keyed on.
  }, [cards, widthOf, files, sizes, innerWidth]);
  const spotOf = (key: string, placement: PlanePlacement | undefined) => placement ?? { ...spots.get(key)! };
  /** A card with no plane line is drawn where it lands once every one of them has been measured:
   *  until then its spot is a guess, and it is not shown. */
  const landed = cards.every(({ item, placement }) => placement || sizes.has(item.key));
  if (landed) for (const { item, placement } of cards) if (!placement) landedAt.current.set(item.key, spots.get(item.key)!);

  const placedBounds = useMemo(
    () =>
      boundsOf(
        cards.map(({ item, view, placement }) => {
          const at = placement ?? spots.get(item.key)!;
          const size = sizes.get(item.key);
          return {
            x: at.x,
            y: at.y,
            w: size?.w ?? widthOf(item.key, view.kind),
            h: size?.h ?? collapsedLinesOf(item.ref, view, files.get(item.ref.ref)) * LINE,
          };
        }),
      ),
    [cards, spots, sizes, widthOf, files],
  );
  const drawn =
    known &&
    nooks.status === 'ready' &&
    cards.length === onCanvas.length &&
    landed &&
    onCanvas.every((i) => !wanted.includes(i.ref.ref) || files.has(i.ref.ref)) &&
    cards.every(({ item }) => sizes.has(item.key));
  const table = useTable(placedBounds, 'desk', drawn);
  const cam = table.cam;
  const press = useRef<Press | null>(null);
  const justDragged = useRef(false);
  const [drag, setDrag] = useState<{ key: string; x: number; y: number } | null>(null);
  /** A sticky's corner being pulled, and the size it is pulled to. */
  const pull = useRef<{ key: string; start: { x: number; y: number }; from: { w: number; h: number } } | null>(null);
  const [sized, setSized] = useState<{ key: string; w: number; h: number } | null>(null);
  const [menu, setMenu] = useState<{ x: number; y: number; item: DeskItem } | null>(null);
  /** A tray card's ⋮, open. */
  const [trayMenu, setTrayMenu] = useState<{ x: number; y: number; row: Row } | null>(null);
  /** What was filed away from the tray here, gone from it before the catalog says so. */
  const [archived, setArchived] = useState<ReadonlySet<string>>(new Set());
  /** A note open in its panel: a placed one by its path, or one started on empty ground, at the
   *  point it was started, until its first word makes it. [tint] is a colour picked before then. */
  const [noting, setNoting] = useState<{
    path: string | null;
    at: { x: number; y: number } | null;
    tint?: StickyTint | TagColorId;
    /** Opened by the bar's Note word, which closes it again. */
    fromBar?: boolean;
  } | null>(null);

  const rawOf = useCallback(
    (path: string): string | null => {
      if (path === DESK_PATH) return containerRaw;
      return pendingText(path) ?? files.get(path) ?? null;
    },
    [containerRaw, files],
  );

  /**
   * One act: every file's change, made on the file as it stands now and queued together. An act
   * that needs a file this page has not read, or the container while the desk cannot be written,
   * writes nothing.
   */
  const act = useCallback(
    (acts: DeskChange[], now = false) => {
      if (acts.some((a) => rawOf(a.path) === null || (a.path === DESK_PATH && !writable))) return;
      const writes = asWrites(acts, rawOf);
      if (writes.length === 0) return;
      const container = writes.find((w) => w.path === DESK_PATH);
      if (container) show(container.text);
      if (now) void commit(writes);
      else write(writes);
    },
    [writable, rawOf, show, write, commit],
  );

  const canvasAt = (clientX: number, clientY: number) =>
    cam.toCanvas(clientX, clientY, surface.current!.getBoundingClientRect());

  /** Where each card is drawn right now, mid-drag included: what a connection runs between. */
  const boxes = useMemo(
    () =>
      new Map(
        cards.map(({ item, view, placement }) => {
          const at = drag?.key === item.key ? drag : (placement ?? spots.get(item.key) ?? { x: 0, y: 0 });
          const size = sizes.get(item.key);
          return [
            item.key,
            { x: at.x, y: at.y, w: size?.w ?? widthOf(item.key, view.kind), h: size?.h ?? heightOf(item.key, item, view) },
          ] as const;
        }),
      ),
    // `heightOf` reads the same sizes and files this is keyed on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cards, drag, spots, sizes, widthOf, files],
  );
  const connect = useLinks({
    boxes,
    links: container?.links ?? [],
    refOf: (key) => members.desk.find((i) => i.key === key)?.ref ?? null,
    canvasAt,
    commit: (next, ends) =>
      act(
        linkOnDesk(
          container?.links ?? [],
          next,
          ends.flatMap((ref) => {
            const at = boxes.get(refKey(ref));
            return at && !placements.has(refKey(ref)) ? [{ ref, at: { x: at.x, y: at.y } }] : [];
          }),
          nooksNow,
        ),
        true,
      ),
    onPress: () => setMenu(null),
  });

  /** A card let go over the open tray goes back on the shelf. */
  const overShelf = (clientX: number, clientY: number) => {
    const r = trayOpen ? trayEl?.getBoundingClientRect() : undefined;
    return !!r && clientX >= r.left && clientX <= r.right && clientY >= r.top && clientY <= r.bottom;
  };

  const onCardDown = (item: DeskItem, at: { x: number; y: number }) => (event: ReactPointerEvent) => {
    if (!writable || event.button !== 0 || cam.wantsPan(event) || !surface.current) return;
    event.stopPropagation();
    setMenu(null);
    event.currentTarget.setPointerCapture(event.pointerId);
    const c = canvasAt(event.clientX, event.clientY);
    press.current = {
      key: item.key,
      grab: { dx: c.x - at.x, dy: c.y - at.y },
      start: { x: event.clientX, y: event.clientY },
      moved: false,
    };
  };

  /** A sticky's corner, pressed. Its first size is the one it is drawn at. */
  const onResizeDown = (key: string) => (event: ReactPointerEvent<HTMLElement>) => {
    if (!writable || event.button !== 0) return;
    event.stopPropagation();
    setMenu(null);
    event.currentTarget.setPointerCapture(event.pointerId);
    const card = event.currentTarget.closest<HTMLElement>('[data-card]');
    if (!card) return;
    pull.current = { key, start: { x: event.clientX, y: event.clientY }, from: { w: card.offsetWidth, h: card.offsetHeight } };
  };

  const onMove = (event: ReactPointerEvent) => {
    if (connect.move(event)) return;
    const r = pull.current;
    if (r) {
      const zoom = cam.camera.zoom;
      const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
      setSized({
        key: r.key,
        w: clamp(r.from.w + (event.clientX - r.start.x) / zoom, STICKY_SIZE.min.w, STICKY_SIZE.max.w),
        h: clamp(r.from.h + (event.clientY - r.start.y) / zoom, STICKY_SIZE.min.h, STICKY_SIZE.max.h),
      });
      return;
    }
    const p = press.current;
    if (!p || !surface.current) return;
    if (!p.moved && Math.hypot(event.clientX - p.start.x, event.clientY - p.start.y) < SLOP) return;
    p.moved = true;
    const at = canvasAt(event.clientX, event.clientY);
    setDrag({ key: p.key, x: at.x - p.grab.dx, y: at.y - p.grab.dy });
  };

  const onUp = (event: ReactPointerEvent) => {
    if (connect.up(event)) return;
    if (pull.current) {
      const item = members.desk.find((i) => i.key === pull.current!.key);
      pull.current = null;
      if (sized && item) act(sizeOnDesk(item, { w: Math.round(sized.w), h: Math.round(sized.h) }, spotOf(item.key, placements.get(item.key)), nooksNow));
      setSized(null);
      return;
    }
    const p = press.current;
    press.current = null;
    if (!p) return;
    if (p.moved) justDragged.current = true;
    const item = members.desk.find((i) => i.key === p.key);
    if (p.moved && drag && item) {
      if (overShelf(event.clientX, event.clientY) && !item.draft) act(deskToShelf(item, nooksNow), true);
      else act(moveOnDesk(item, table.keepInView({ x: drag.x, y: drag.y }), nooksNow));
    }
    setDrag(null);
  };

  /** A press that moved the card is a drag and opens nothing. */
  const showDetail = (ref: PlaneRef, where: 'table' | 'tray') => {
    setMenu(null);
    setNoting(null);
    setDetail((was) => (was && refKey(was.ref) === refKey(ref) && was.where === where ? null : { ref, where }));
  };
  const openCard = (item: DeskItem) => {
    if (justDragged.current) {
      justDragged.current = false;
      return;
    }
    const view = cards.find((c) => c.item.key === item.key)?.view;
    if (view?.kind === 'note') openNoteCard(item);
    else showDetail(item.ref, 'table');
  };

  /** A note on the table opens in its panel, never in the details. */
  const openNoteCard = (item: DeskItem) => {
    setMenu(null);
    setDetail(null);
    setNoting({ path: item.ref.ref, at: null });
  };

  const setOpenOut = (item: DeskItem, open: boolean) => {
    if (!known) return;
    if (!writable) {
      setOpenHere((was) => {
        const next = new Set(was);
        if (open) next.add(item.key);
        else next.delete(item.key);
        return next;
      });
      return;
    }
    if (open) {
      setOpening(item.key);
      return;
    }
    const view = cards.find((c) => c.item.key === item.key)?.view;
    const lines = view && collapsedLinesOf(item.ref, view, files.get(item.ref.ref));
    act(openOnDesk(item, spotOf(item.key, placements.get(item.key)), false, nooksNow, lines));
  };

  const latest = useRef({ act, spotOf, placements });
  latest.current = { act, spotOf, placements };

  useEffect(() => {
    if (!opening) return;
    const el = [...(surface.current?.querySelectorAll<HTMLElement>('[data-card-key]') ?? [])].find(
      (e) => e.dataset['cardKey'] === opening,
    );
    const item = members.desk.find((i) => i.key === opening);
    if (!el || !item) {
      setOpening(null);
      return;
    }
    return whenHeightSettles(el, (height) => {
      setOpening(null);
      const now = latest.current;
      now.act(openOnDesk(item, now.spotOf(item.key, now.placements.get(item.key)), true, nooksNow, height / LINE));
    });
    // Restarted only by a new opening: re-running on every render would reset the settling count.
  }, [opening]);

  const onDragOver = (event: DragEvent) => {
    if (!writable || !event.dataTransfer.types.includes(REF_MIME)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  };

  /** A card let go from the shelf is put down where it lands. */
  const onDrop = (event: DragEvent) => {
    const data = event.dataTransfer.getData(REF_MIME);
    if (!data || !surface.current || !writable) return;
    event.preventDefault();
    const dropped = JSON.parse(data) as PlaneRef;
    const key = refKey(dropped);
    if (members.desk.some((i) => i.key === key)) return;
    const from = members.shelf.find((i) => i.key === key);
    const drafted = members.drafts.find((i) => i.key === key);
    const at = canvasAt(event.clientX, event.clientY);
    const place = table.keepInView({ x: at.x - widthOfRef(dropped) / 2, y: at.y - LINE });
    act(drafted ? keepDraft(drafted, place, nooksNow) : from ? shelfToDesk(from, place, nooksNow) : placeOnDesk(dropped, place, nooksNow), true);
  };

  /** How wide a card of [ref] is drawn, from what the vault says it is. */
  const widthOfRef = (ref: PlaneRef): number => {
    if (workspaces.some((w) => w.path === ref.ref)) return WIDTH.nook;
    const kind = isArcIn(ref.ref, arcs) ? 'arc' : itemFor(ref.ref).kind === 'recording' ? 'recording' : 'note';
    return WIDTH[kind];
  };

  /** The first free gap for a card [w] wide, from the top-left of what is on the table. */
  const gapFor = (w: number, h: number) => {
    const boxes = cards.map(({ item, view, placement }) => {
      const at = placement ?? spots.get(item.key) ?? { x: 0, y: 0 };
      const size = sizes.get(item.key);
      return { x: at.x, y: at.y, w: size?.w ?? widthOf(item.key, view.kind), h: size?.h ?? h };
    });
    return freeSpots(boxes, [{ key: 'new', w, h }], innerWidth).get('new')!;
  };

  /** The tag colour a card takes from its first tag. */
  const hueOf = (item: DeskItem) => {
    const tag = item.nook?.tags[0] ?? catalog.get(item.ref.ref)?.tags[0];
    return tag ? tagTint(registries, tag) : null;
  };
  const fillOf = (item: DeskItem, placement?: PlanePlacement): string | undefined => {
    if (placement?.tagTint) return tagTones(theme, placement.tagTint).band;
    const hue = hueOf(item);
    return hue ? tagTones(theme, hue).band : undefined;
  };
  /** What a note's sticky on the table wears, for its card in the tray; nothing for one not on it. */
  const stickyFillOf = (ref: PlaneRef): string | undefined => {
    const placement = placements.get(refKey(ref));
    if (!placement) return undefined;
    const item = members.desk.find((i) => i.key === refKey(ref));
    return (item && fillOf(item, placement)) ?? `var(${placement.tint === 'green' ? '--stickyGreen' : '--stickyWarm'})`;
  };

  /** A card taken off the table, from its menu or ⌫; a nook leaves by its pin, a draft by Let go. */
  const removeFromDesk = (item: DeskItem) => {
    if (!writable || item.nook || item.draft) return;
    if (detail && refKey(detail.ref) === item.key) setDetail(null);
    if (noting?.path === item.ref.ref) setNoting(null);
    act(offDesk(item, nooksNow), true);
  };

  /** A recording or a note filed away from its tray card: off the desk and the shelf as well. */
  const archiveEntry = (row: Row) => {
    const key = refKey(row.ref);
    const listed = [...members.desk, ...members.shelf].some((i) => i.key === key);
    if (detail && refKey(detail.ref) === key) setDetail(null);
    if (noting?.path === row.ref.ref) setNoting(null);
    setArchived((was) => new Set(was).add(key));
    act(archiveFromDesk(row.ref, listed, nooksNow), true);
  };

  const arcsByName = useMemo(() => (arcs ? new Map(arcs.map((a) => [a.name, a])) : null), [arcs]);
  const rowOf = (i: DeskItem): Row => ({
    ref: i.ref,
    title: i.nook
      ? nameOf(i.nook, catalog).name
      : (cardName(cardView(i.ref, arcs, files, undefined, catalog) ?? undefined) ?? arcTitleOf(i.ref.ref)),
    nook: i.nook ?? undefined,
    status: arcs?.find((a) => a.path === i.ref.ref)?.status,
  });
  const deskKeys = useMemo(() => new Set(members.desk.map((i) => i.key)), [members.desk]);
  const itemOfRow = (row: Row) => [...members.drafts, ...members.shelf].find((i) => i.key === refKey(row.ref));
  const atOf = (row: Row): Date | null => {
    const arc = arcs?.find((a) => a.path === row.ref.ref);
    if (arc) {
      const at = recencyOf(arc);
      return at > 0 ? new Date(at) : null;
    }
    return itemFor(row.ref.ref).at;
  };
  const trayEntries = useMemo((): TrayEntry[] => {
    if (!trayOpen) return [];
    const text = query.trim().toLowerCase();
    if (kind === 'drafts') return trayShown.map((i) => ({ row: rowOf(i), at: null, placed: false }));
    if (kind === 'shelf') {
      const rows = members.shelf.map(rowOf);
      return (text ? rows.filter((r) => r.title.toLowerCase().includes(text)) : rows).map((row) => ({ row, at: null, placed: deskKeys.has(refKey(row.ref)) }));
    }
    const filedAway = (row: Row) => {
      if (archived.has(refKey(row.ref))) return true;
      const text = files.get(row.ref.ref);
      return !!text && String(parseEntity(row.ref.ref, text).frontMatter['visibility'] ?? '').trim() === 'archived';
    };
    return vaultRows.filter((row) => !filedAway(row)).map((row) => ({ row, at: atOf(row), placed: deskKeys.has(refKey(row.ref)) }));
    // rowOf and atOf read the same files, plan and catalog this is keyed on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trayOpen, kind, query, trayShown, members.shelf, vaultRows, deskKeys, arcs, catalog, files, archived]);

  const openEntry = (row: Row) => {
    const view = cardView(row.ref, arcs, files, undefined, catalog);
    const onTable = members.desk.find((i) => i.key === refKey(row.ref));
    if (onTable) return openCard(onTable);
    if (view?.kind === 'note') {
      setMenu(null);
      setDetail(null);
      setNoting({ path: row.ref.ref, at: gapFor(WIDTH.note, 120) });
    } else showDetail(row.ref, 'tray');
  };
  const trayCard = ({ row, placed }: TrayEntry) => {
    const item = itemOfRow(row);
    if (row.nook) {
      return (
        <NookCard
          cardKey={refKey(row.ref)}
          view={{ kind: 'nook', title: row.title, plane: row.nook.plane, nook: row.nook }}
          left={0}
          top={0}
          size={null}
          fluid
          menuOpen={item ? menu?.item.key === item.key : false}
          dragging={false}
          onCanvas={placed}
          onPointerDown={() => {}}
          onOpen={() => openEntry(row)}
          onRemove={() => {}}
          onMenu={(x, y) => item && setMenu({ x, y, item })}
        />
      );
    }
    if (isArcIn(row.ref.ref, arcs)) {
      const view = cardView(row.ref, arcs, files, undefined, catalog);
      return view ? (
        <NookCard cardKey={refKey(row.ref)} view={view} left={0} top={0} size={null} fluid dragging={false} onCanvas={placed} onPointerDown={() => {}} onOpen={() => openEntry(row)} onRemove={() => {}} onMenu={() => {}} />
      ) : null;
    }
    const text = files.get(row.ref.ref) ?? null;
    return (
      <NookItemCard
        path={row.ref.ref}
        anchor={row.ref.anchors[0]}
        kind={itemKind(row.ref.ref, text) === 'note' ? 'note' : 'recording'}
        text={text}
        nookTags={[]}
        placed={placed}
        fill={itemKind(row.ref.ref, text) === 'note' ? stickyFillOf(row.ref) : undefined}
        onMore={writable ? (p) => setTrayMenu({ ...p, row }) : undefined}
        onOpen={() => openEntry(row)}
      />
    );
  };

  const [recent, setRecent] = useState<RecentRecording[] | null>(null);
  useEffect(() => {
    if (!making || state.status !== 'ready') return;
    let cancelled = false;
    const latest = corpus.filter((item) => item.kind === 'recording').slice(0, 20).map((item) => item.path);
    void state.vault.readFiles(latest).then((read) => {
      if (cancelled) return;
      const text = new Map(read.map((r) => [r.path, r.text]));
      setRecent(
        latest.flatMap((p) => {
          const raw = text.get(p);
          return raw ? [{ path: p, date: capturedLabel(p, raw), face: recordingFace(p, raw, undefined) }] : [];
        }),
      );
    });
    return () => {
      cancelled = true;
    };
  }, [making, state, corpus]);
  useEffect(() => {
    if (kind !== 'arcs' || !trayOpen) setMaking(false);
  }, [kind, trayOpen]);

  /** A new arc: its file and its place on the desk, in the first free gap, written together. */
  const makeArc = (arc: NewArc) => {
    const file = newArcFile(arc);
    if (!file || !writable) return;
    const acts = placeOnDesk({ ref: file.path, anchors: [] }, gapFor(WIDTH.arc, 60), nooksNow);
    const container = asWrites(acts, rawOf).find((w) => w.path === DESK_PATH);
    if (!container) return;
    show(container.text);
    write([{ ...file, act: file.path }, ...asWrites(acts, rawOf).map((w) => ({ ...w, act: file.path }))]);
    setMaking(false);
  };

  /** Note: the note's own panel, hung off the first free gap its sticky will stand
   *  in, the first word making the note there. */
  const openNote = () => {
    setDetail(null);
    setNoting((was) => (was?.fromBar ? null : { path: null, at: gapFor(WIDTH.note, 120), fromBar: true, tint: FIRST_TINT }));
  };
  const mintNote = (first: string, where: { x: number; y: number }): MintedNote | null => {
    if (state.status !== 'ready' || !writable) return null;
    const now = new Date();
    const taken = new Set(state.vault.listPaths(monthPrefix(now)));
    const fresh = newNotePath(now, (c) => taken.has(c) || files.has(c));
    const note = newHeldNoteFile(first, newUlid(now.getTime()));
    const acts = placeOnDesk({ ref: fresh, anchors: [] }, where, nooksNow);
    const writes = asWrites(acts, rawOf);
    const container = writes.find((w) => w.path === DESK_PATH);
    if (!container) return null;
    show(container.text);
    write([{ path: fresh, text: note }, ...writes]);
    setFiles((m) => new Map(m).set(fresh, note));
    setNoting((n) => n && { ...n, path: fresh });
    return { path: fresh, raw: note };
  };

  const togglePin = (item: DeskItem) => {
    if (!item.nook) return;
    if (!item.pinned) return void pin(item.nook);
    if (writable) act(unpinOnDesk(item, nooksNow), true);
    else if (known) act(unpinWithoutContainer(item), true);
  };

  const ground = canvasGround(theme, null);
  const grid = 28 * cam.camera.zoom;

  /** What Esc closes: a menu first, then whatever opened last, and then full canvas. */
  const openOrder = useOpenOrder({ tray: trayOpen, making: trayOpen && making, detail: detail !== null, noting: noting !== null, link: connect.on });
  useEffect(() => {
    const close: Record<string, () => void> = {
      tray: () => setTrayOpen(false),
      making: () => setMaking(false),
      detail: () => setDetail(null),
      noting: () => setNoting(null),
      link: () => connect.setOn(false),
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      const last = openOrder[openOrder.length - 1];
      if (menu) setMenu(null);
      else if (trayMenu) setTrayMenu(null);
      else if (connect.selected) connect.setSelected(null);
      else if (last) close[last]!();
      else if (table.full) table.setFull(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menu, trayMenu, openOrder, connect.selected, connect.setSelected, connect.setOn, table]);

  const notingItem = noting?.path ? (members.desk.find((i) => i.ref.ref === noting.path && i.ref.anchors.length === 0) ?? null) : null;
  const notingKey = notingItem?.key ?? null;
  const notingPlacement = notingItem ? placements.get(notingItem.key) : undefined;
  useEffect(() => {
    if (!noting?.tint || !notingItem) return;
    act(tintOnDesk(notingItem, noting.tint, spotOf(notingItem.key, notingPlacement), nooksNow), true);
    setNoting((n) => n && { ...n, tint: undefined });
    // Only the pending colour and the note standing are reasons to run.
  }, [noting?.tint, notingItem?.key]);

  useCloseOnGround(detail !== null || noting !== null, () => {
    setDetail(null);
    setNoting(null);
  });
  const win = pageSize();
  const detailsPlace = (() => {
    const rect = trayOpen ? trayEl?.getBoundingClientRect() : undefined;
    const main = document.querySelector('.nook-main')?.getBoundingClientRect();
    if (!rect || !main) return hangUnder({ left: 40 }, win);
    return hangLeftOf(rect.left - main.left, win);
  })();

  /** The details of what was pressed, hung off its card on the table or, with no card,
   *  from the bar. Its page is the panel's Full screen, one press further. */
  const detailPanel = (() => {
    if (!detail) return null;
    const ref = detail.ref;
    const key = refKey(ref);
    const text = files.get(ref.ref) ?? null;
    const nook = workspaces.find((w) => w.path === ref.ref) ?? null;
    const arc = arcs?.find((a) => a.path === ref.ref) ?? null;
    const close = () => setDetail(null);
    const full = { to: hrefFor(ref.ref) };
    let node: ReactNode = null;
    let label = 'Details';
    if (nook) {
      label = 'Nook';
      const hue = nook.tags[0] ? tagTint(registries, nook.tags[0]) : null;
      node = (
        <NookWindow
          nook={nook}
          name={nameOf(nook, catalog).named ? nameOf(nook, catalog).name : ''}
          arcsByName={arcsByName}
          ink={markInk(theme, hue)}
          own={false}
          startWith={startWithOf(nook.startWith, arcsByName ? heldArcsOf(nook, arcsByName) : null)}
          onOpenArc={(a) => navigate(hrefFor(a.path))}
          onOpenHeld={(held) => openEntry({ ref: held, title: '' })}
          onClose={close}
        />
      );
    } else if (arc) {
      label = 'Arc';
      node = <ArcWindow arc={arc} onClose={close} />;
    } else if (text !== null) {
      const view = cardView(ref, arcs, files, undefined, catalog);
      if (itemKind(ref.ref, text) === 'recording') {
        label = 'Recording';
        node = (
          <RecordingWindow
            path={ref.ref}
            raw={text}
            anchor={ref.anchors[0]}
            inArcs={arcsCiting(ref.ref, arcs)}
            onOpenArc={(a) => navigate(hrefFor(a.path))}
            fullScreen={full}
            onClose={close}
          />
        );
      } else {
        label = 'Note';
        node = (
          <NoteWindow
            title={view?.kind === 'note' ? view.title : null}
            text={view?.kind === 'note' ? view.text : ''}
            fullScreen={full}
            onClose={close}
          />
        );
      }
    }
    if (!node) return null;
    if (detail.where === 'table' && cards.some((c) => c.item.key === key)) {
      return (
        <CardHang table={table} surface={surface} cardKey={key} kind="details" label={label}>
          {node}
        </CardHang>
      );
    }
    return (
      <BarHang kind="details" label={label} place={detailsPlace}>
        {node}
      </BarHang>
    );
  })();

  return (
    <div className="nook desk-page" data-desk>
      <div className="nook-main">
        <PageBar words={[]} quiet={known && !writable ? 'Read-only until the phone has set up the desk' : undefined} />

        <div className={`table-scroll${table.full ? ' is-full' : ''}`}>
          <div className="desk-top">
            <div className="desk-table">
            <Table
              table={table}
              tools={
                <>
                  <NoteButton
                    note={{
                      pressed: noting?.fromBar === true,
                      onPress: writable ? openNote : undefined,
                      title: known && !writable ? 'Read-only until the phone has set up the desk' : undefined,
                    }}
                  />
                  <TrayBrowse open={trayOpen} onPress={() => setTrayOpen((on) => !on)} />
                  <LinkButton
                    on={connect.on}
                    onPress={writable ? () => connect.setOn((on) => !on) : undefined}
                    title={known && !writable ? 'Read-only until the phone has set up the desk' : undefined}
                  />
                </>
              }
              surfaceRef={surface}
              onDragOver={onDragOver}
              onDrop={onDrop}
              className={`nook-plane${drag ? ' dragging' : ''}`}
              style={
                {
                  backgroundSize: `${grid}px ${grid}px`,
                  backgroundPosition: `${cam.camera.x}px ${cam.camera.y}px`,
                  '--dot': ground.dot,
                } as CSSProperties
              }
              onPointerDown={() => {
                setMenu(null);
                connect.setSelected(null);
              }}
              onPointerMove={onMove}
              onPointerUp={onUp}
              onDoubleClickEmpty={
                writable
                  ? (event) => {
                      const at = canvasAt(event.clientX, event.clientY);
                      setMenu(null);
                      setDetail(null);
                      setNoting({ at: { x: at.x - WIDTH.note / 2, y: at.y - 24 }, path: null, tint: FIRST_TINT });
                    }
                  : undefined
              }
            >
              {connect.layer}
              {cards.map(({ item, view, placement }) => {
                const spot = spotOf(item.key, placement);
                const at = drag?.key === item.key ? drag : spot;
                const open = isOpen(item.key);
                const unplaced = !placement && !landed ? 'desk-landing' : '';
                return (
                  <NookCard
                    key={item.key}
                    cardKey={item.key}
                    view={view}
                    left={at.x}
                    top={at.y}
                    size={
                      view.kind !== 'note'
                        ? null
                        : sized?.key === item.key
                          ? sized
                          : placement?.w !== undefined && placement.h !== undefined
                            ? { w: placement.w, h: placement.h }
                            : null
                    }
                    fill={view.kind === 'nook' ? undefined : fillOf(item, placement)}
                    menuOpen={menu?.item.key === item.key}
                    dragging={drag?.key === item.key}
                    open={open}
                    selected={(detail?.where === 'table' && refKey(detail.ref) === item.key) || notingKey === item.key}
                    className={unplaced}
                    onPointerDown={onCardDown(item, at)}
                    onResizeDown={writable && view.kind === 'note' ? onResizeDown(item.key) : undefined}
                    onHandleDown={writable ? connect.handlesOf(item.key) : undefined}
                    linkTarget={connect.target === item.key}
                    onOpen={() => openCard(item)}
                    onRemove={() => removeFromDesk(item)}
                    onMenu={(x, y) => setMenu({ x, y, item })}
                    asCard={
                      view.kind === 'recording'
                        ? (shell, openProps) => (
                            <NookItemCard
                              path={item.ref.ref}
                              anchor={item.ref.anchors[0]}
                              kind="recording"
                              text={files.get(item.ref.ref) ?? null}
                              nookTags={[]}
                              minimized={!open}
                              onExpand={() => setOpenOut(item, true)}
                              onMore={(p) => setMenu({ ...p, item })}
                              shell={shell}
                              openProps={openProps}
                              onOpen={() => openCard(item)}
                            />
                          )
                        : undefined
                    }
                  />
                );
              })}
            </Table>
            </div>

            <Presence>
            {trayOpen && (
              <Tray
                className="desk-tray"
                kinds={kinds}
                kind={kind}
                onKind={setTrayKind}
                lifted={trayLifted}
                onToggleLift={toggleLift}
                onClose={() => setTrayOpen(false)}
                query={query}
                onQuery={setQuery}
                full={table.full}
                maxHeight={table.height}
                trayRef={setTrayEl}
              >
                {kind === 'arcs' && writable && !query && (
                  <button type="button" className="tray-new" onClick={() => setMaking(true)}>
                    New arc
                  </button>
                )}
                <TrayList
                  key={kind}
                  entries={trayEntries}
                  query={query}
                  recent={dated}
                  older={kind === 'drafts' && !allDrafts && tray.older.length > 0 ? { count: tray.older.length, onMore: () => setAllDrafts(true) } : undefined}
                  carrying={writable}
                  renderCard={trayCard}
                />
              </Tray>
            )}
            </Presence>
          </div>

        </div>

        <Presence>
        {trayOpen && kind === 'arcs' && making && (
          <BarHang kind="list" label="New arc" place={hangUnder({ left: 40 }, win, 460)} width={460}>
            <NewArcFlow
              recordings={recent}
              taken={(n) => nameTaken(n, arcs ?? [])}
              onClose={() => setMaking(false)}
              onCreate={makeArc}
            />
          </BarHang>
        )}
        </Presence>
        <Presence>
        {noting && (notingItem || noting.at) && (
          <StickyPanel
            table={table}
            surface={surface}
            theme={theme}
            path={noting.path}
            cardKey={notingItem ? notingItem.key : null}
            at={noting.at}
            tone={noteTone(notingPlacement ?? pendingTint(noting.tint), notingItem ? hueOf(notingItem) : null, theme)}
            tint={notingPlacement?.tagTint ?? notingPlacement?.tint ?? noting.tint ?? null}
            onTint={(tint) =>
              notingItem
                ? act(tintOnDesk(notingItem, tint, spotOf(notingItem.key, notingPlacement), nooksNow), true)
                : setNoting((n) => n && { ...n, tint })
            }
            mint={(first) => (noting.at ? mintNote(first, noting.at) : null)}
            onChange={(path, text) => setFiles((m) => new Map(m).set(path, text))}
            onClose={() => setNoting(null)}
          />
        )}
        </Presence>
        <Presence>{detailPanel}</Presence>
      </div>

      {menu && (
        <DeskCardMenu
          at={menu}
          item={menu.item}
          view={cards.find((c) => c.item.key === menu.item.key)?.view}
          open={isOpen(menu.item.key)}
          writable={writable}
          known={known}
          onFold={() => setOpenOut(menu.item, !isOpen(menu.item.key))}
          onPin={() => togglePin(menu.item)}
          onKeep={() =>
            act(
              keepDraft(
                menu.item,
                cards.some((c) => c.item.key === menu.item.key)
                  ? spotOf(menu.item.key, placements.get(menu.item.key))
                  : gapFor(WIDTH.nook, 120),
                nooksNow,
              ),
              true,
            )
          }
          onLetGo={() => act(letGoDraft(menu.item), true)}
          onShelve={() => act(deskToShelf(menu.item, nooksNow), true)}
          onRemove={members.desk.some((i) => i.key === menu.item.key) ? () => removeFromDesk(menu.item) : undefined}
          onOpen={() => showDetail(menu.item.ref, 'table')}
          onClose={() => setMenu(null)}
        />
      )}
      {trayMenu && (
        <TrayCardMenu
          at={trayMenu}
          onOpen={() => openEntry(trayMenu.row)}
          onArchive={() => archiveEntry(trayMenu.row)}
          onClose={() => setTrayMenu(null)}
        />
      )}
    </div>
  );
}

/** What a card on the desk offers: fold it, pin it, keep a draft, put it on the shelf. */
function DeskCardMenu({
  at,
  item,
  view,
  open,
  writable,
  known,
  onFold,
  onPin,
  onKeep,
  onLetGo,
  onShelve,
  onRemove,
  onOpen,
  onClose,
}: {
  at: { x: number; y: number };
  item: DeskItem;
  view: CardView | undefined;
  open: boolean;
  writable: boolean;
  /** Whether the container's read has an outcome: an unpin and a fold differ by it. */
  known: boolean;
  onFold: () => void;
  onPin: () => void;
  onKeep: () => void;
  onLetGo: () => void;
  onShelve: () => void;
  /** Offered on a card on the table that is neither a nook nor a draft. */
  onRemove?: () => void;
  onOpen: () => void;
  onClose: () => void;
}) {
  useEffect(() => {
    const away = (e: PointerEvent) => {
      if (!(e.target as HTMLElement).closest('.plane-menu')) onClose();
    };
    const id = setTimeout(() => document.addEventListener('pointerdown', away));
    return () => {
      clearTimeout(id);
      document.removeEventListener('pointerdown', away);
    };
  }, [onClose]);

  const run = (fn: () => void) => () => {
    fn();
    onClose();
  };
  const name = cardName(view);
  const foldable = view?.kind === 'recording' || view?.kind === 'nook';

  return (
    <div className="plane-menu" style={{ left: at.x, top: at.y }} role="menu">
      <button type="button" role="menuitem" onClick={run(onOpen)}>
        Open
      </button>
      {foldable && known && (
        <button type="button" role="menuitem" onClick={run(onFold)}>
          {open ? 'Minimize' : 'Expand'}
        </button>
      )}
      {item.nook && (known || !item.pinned) && (
        <button type="button" role="menuitem" onClick={run(onPin)}>
          {item.pinned ? 'Unpin' : 'Pin'}
        </button>
      )}
      {item.draft && writable && (
        <button type="button" role="menuitem" onClick={run(onKeep)}>
          Keep
        </button>
      )}
      {item.draft && known && (
        <button type="button" role="menuitem" onClick={run(onLetGo)}>
          Let go
        </button>
      )}
      {!item.draft && writable && (
        <button type="button" role="menuitem" onClick={run(onShelve)}>
          Move to the shelf
        </button>
      )}
      {name && (
        <button type="button" role="menuitem" onClick={run(() => void navigator.clipboard?.writeText(name))}>
          Copy name
        </button>
      )}
      {onRemove && writable && !item.nook && !item.draft && (
        <button type="button" role="menuitem" onClick={run(onRemove)}>
          Remove from canvas <kbd className="kbd">⌫</kbd>
        </button>
      )}
    </div>
  );
}
